import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import {
  MdmSearchOfficeResultRowValue,
  OfficeDetailsResponse,
} from "../../../pages/mdmOfficeSearch/modelsAndSchemas/models";
import apiInstance from "../../instances/apiInstance";
import {
  BillingSalesOfficeType,
  DeliveryOfficeListItem,
  InvoiceTemplateType,
  MdmCreateOfficeParams,
  MdmSearchOfficeParams,
  MdmUpdateOfficeParams,
} from "./models";

export const useGetMdmOfficeById = () => {
  return useMutation({
    mutationFn: async (officeId: string) => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getMdmOfficeById(officeId)}`,
      );
      return res.data as OfficeDetailsResponse;
    },
  });
};

export const useCreateMdmOfficeMutation = () => {
  return useMutation({
    mutationFn: (params: MdmCreateOfficeParams) => {
      return apiInstance.post(API_ENDPOINTS.CREATE_MDM_OFFICE, params);
    },
  });
};

export const useUpdateMdmOfficeById = () => {
  return useMutation({
    mutationFn: (params: MdmUpdateOfficeParams) => {
      return apiInstance.put(
        API_ENDPOINTS.updateMdmOfficeById(params.officeId),
        params,
      );
    },
  });
};

export const useDeleteMdmOfficeMutation = () => {
  return useMutation({
    mutationFn: (officeId: string) => {
      return apiInstance.delete(API_ENDPOINTS.deleteMdmOffice(officeId));
    },
  });
};

export const useGetMarketsQuery = () => {
  return useQuery({
    queryKey: ["getMarkets"],
    queryFn: async () => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_MARKETS);
      return res.data;
    },
    retry: false,
  });
};

export const useGetInvoiceTemplatesQuery = (
  invoiceTemplateType?: InvoiceTemplateType,
) => {
  return useQuery({
    queryKey: ["getInvoiceTemplates", invoiceTemplateType],
    queryFn: async () => {
      let url = API_ENDPOINTS.GET_INVOICE_TEMPLATES;
      const queryParams = new URLSearchParams();
      if (invoiceTemplateType) {
        queryParams.append("templateTypeId", invoiceTemplateType);
        url = `${API_ENDPOINTS.GET_INVOICE_TEMPLATES}?${queryParams.toString()}`;
      }
      const res = await apiInstance.get(url);
      return res.data;
    },
    retry: false,
  });
};

export const useGetSearchRegionMutation = () => {
  return useMutation({
    mutationFn: async (marketId: string) => {
      const res = await apiInstance.post(API_ENDPOINTS.GET_SEARCH_REGION, {
        marketId,
      });
      return res.data;
    },
  });
};

export const useGetBillingSalesOfficeQuery = (
  billingOfficeSalesType?: BillingSalesOfficeType,
) => {
  return useQuery({
    queryKey: ["getBillingOfficeSalesType", billingOfficeSalesType],
    queryFn: async () => {
      let url = API_ENDPOINTS.GET_OFFICE_LIST;
      const queryParams = new URLSearchParams();
      if (billingOfficeSalesType) {
        queryParams.append("officeTypeId", billingOfficeSalesType);
        url = `${API_ENDPOINTS.GET_OFFICE_LIST}?${queryParams.toString()}`;
      }
      const res = await apiInstance.get(url);
      return res.data;
    },
    retry: false,
  });
};

export const useGetDeliveryOfficeListQuery = () => {
  const [params, setParams] = useState<{
    countryCode?: string;
  }>({});

  const query = useQuery({
    queryKey: ["getDeliveryOfficeList", params.countryCode],
    queryFn: async (): Promise<DeliveryOfficeListItem[]> => {
      let url = API_ENDPOINTS.GET_OFFICE_LIST;
      const queryParams = new URLSearchParams();
      if (params.countryCode) {
        queryParams.append("countryCode", params.countryCode);
      }
      if (queryParams.toString()) {
        url = `${url}?${queryParams.toString()}`;
      }
      const res = await apiInstance.get(url);
      return res.data as DeliveryOfficeListItem[];
    },
    enabled: !!params,

    retry: false,
  });

  const refetchWithParams = (countryCode?: string) => {
    setParams({ countryCode });
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useMdmSearchOfficeMutation = () => {
  return useMutation({
    mutationFn: async (params: MdmSearchOfficeParams) => {
      const res = await apiInstance.post(
        API_ENDPOINTS.GET_MDM_OFFICES_SEARCH,
        params,
      );
      return res.data as MdmSearchOfficeResultRowValue[];
    },
    retry: false,
  });
};

export const useGetBusinessUnitsByCountryQuery = () => {
  const [params, setParams] = useState<string>();
  const query = useQuery({
    queryKey: ["getBusinessUnitsByCountry", params],
    queryFn: async () => {
      if (!params) return [];
      const url = API_ENDPOINTS.GET_BUSINESS_UNITS_BY_COUNTRY(params);
      const res = await apiInstance.get(url);
      return res.data;
    },
    enabled: !!params,
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};
